import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { notificacionService } from '../services/notificacionService'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Listbox from '../components/ui/Listbox'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import PushNotificationButton from '../components/PushNotificationButton'
import { 
  Bell, 
  Search, 
  Filter, 
  Check, 
  CheckCheck, 
  Trash2, 
  EyeOff,
  RefreshCw, 
  AlertCircle,
  Calendar,
  X,
  Mail,
  MailOpen,
  AlertTriangle,
  Info,
  MessageSquare
} from 'lucide-react'
// Función helper para formatear fechas
const formatDate = (dateString, formatType = 'short') => {
  if (!dateString) return '-'
  
  try {
    const date = new Date(dateString)
    
    // Validar que la fecha sea válida
    if (isNaN(date.getTime())) {
      return '-'
    }
    
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
    const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
    
    if (formatType === 'long') {
      const dayName = days[date.getDay()]
      const day = date.getDate()
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${dayName}, ${day} de ${month} de ${year} a las ${hours}:${minutes}`
    } else {
      const day = date.getDate()
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day} de ${month}, ${year} a las ${hours}:${minutes}`
    }
  } catch (error) {
    console.error('Error formateando fecha:', error)
    return '-'
  }
}

export default function Notificaciones() {
  const { user } = useAuth()
  const { 
    contadorNoLeidas, 
    marcarLeida: marcarLeidaContext,
    marcarTodasLeidas: marcarTodasLeidasContext,
    ocultarTodasNotificaciones,
    eliminarNotificacion: eliminarNotificacionContext,
    ocultarNotificacion: ocultarNotificacionContext,
    registerRefreshListener,
    fetchContador
  } = useNotifications()
  const [notificaciones, setNotificaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    search: '',
    leida: '',
    tipo_notificacion: '',
    prioridad: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [selectedNotificacion, setSelectedNotificacion] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [marcandoLeida, setMarcandoLeida] = useState(null)
  const [marcandoTodas, setMarcandoTodas] = useState(false)
  const [eliminando, setEliminando] = useState(null)
  const [ocultando, setOcultando] = useState(null)

  // Función para refrescar notificaciones
  const refreshNotifications = useCallback(() => {
    fetchNotificaciones()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Refrescar solo cuando cambian los filtros o paginación (no automáticamente)
  useEffect(() => {
    if (user) {
      // Cargar datos inicialmente
      fetchNotificaciones()
      
      // Registrar listener para refrescar cuando se marque como leída en otro lugar
      const unregister = registerRefreshListener(refreshNotifications)
      
      return () => {
        unregister()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]) // Solo cuando cambia el usuario

  // Refrescar cuando cambian filtros o paginación
  useEffect(() => {
    if (user) {
      fetchNotificaciones()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, filtros.leida, filtros.tipo_notificacion, filtros.prioridad])

  // Refrescar cuando cambia el search (con debounce)
  useEffect(() => {
    if (user) {
      if (filtros.search) {
        const timer = setTimeout(() => {
          fetchNotificaciones()
        }, 500)
        return () => clearTimeout(timer)
      } else {
        fetchNotificaciones()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.search])

  const fetchNotificaciones = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.leida !== '' && { leida: filtros.leida }),
        ...(filtros.tipo_notificacion && { tipo_notificacion: filtros.tipo_notificacion }),
        ...(filtros.prioridad && { prioridad: filtros.prioridad })
      }

      const response = await notificacionService.getAll(params)

      if (response.success) {
        const paginatedData = response.data
        if (paginatedData && paginatedData.data) {
          setNotificaciones(paginatedData.data)
          setPagination({
            current_page: paginatedData.current_page || 1,
            per_page: paginatedData.per_page || 15,
            total: paginatedData.total || 0,
            last_page: paginatedData.last_page || 1
          })
        } else if (Array.isArray(paginatedData)) {
          setNotificaciones(paginatedData)
          setPagination({
            current_page: 1,
            per_page: paginatedData.length,
            total: paginatedData.length,
            last_page: 1
          })
        } else {
          setNotificaciones([])
        }
      } else {
        setError(response.error || 'Error al cargar notificaciones')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching notificaciones:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleMarcarLeida = async (notificacion) => {
    const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion
    setMarcandoLeida(notificacionId)
    setError(null)

    const success = await marcarLeidaContext(notificacionId)

    if (success) {
      // Actualizar la lista local
      await fetchNotificaciones()
      // Actualizar el modal si está abierto
      if (selectedNotificacion && (selectedNotificacion.id_notificacion || selectedNotificacion.idNotificacion) === notificacionId) {
        setSelectedNotificacion({ ...notificacion, leida: true })
      }
    } else {
      setError('Error al marcar notificación como leída')
    }
    
    setMarcandoLeida(null)
  }

  const handleMarcarTodasLeidas = async () => {
    setMarcandoTodas(true)
    setError(null)

    const success = await marcarTodasLeidasContext()

    if (success) {
      // Actualizar la lista local
      await fetchNotificaciones()
    } else {
      setError('Error al marcar todas las notificaciones como leídas')
    }
    
    setMarcandoTodas(false)
  }

  const handleBorrarTodas = async () => {
    if (!window.confirm('¿Borrar todas las notificaciones? (Soft delete: se ocultan y no se eliminan permanentemente.)')) {
      return
    }
    setMarcandoTodas(true)
    setError(null)
    const success = await ocultarTodasNotificaciones()
    if (success) {
      await fetchNotificaciones()
      await fetchContador()
      if (showDetailModal) {
        setShowDetailModal(false)
        setSelectedNotificacion(null)
      }
    } else {
      // Fallback: borrar en lote lo cargado (compatible con backends sin /ocultar-todas).
      const ids = notificaciones
        .map((n) => n.id_notificacion || n.idNotificacion)
        .filter(Boolean)
      if (ids.length === 0) {
        setError('No se pudieron borrar todas las notificaciones')
      } else {
        await Promise.allSettled(ids.map((id) => notificacionService.deleteSoft(id)))
        await fetchNotificaciones()
        await fetchContador()
      }
    }
    setMarcandoTodas(false)
  }

  const handleOcultar = async (notificacion) => {
    if (!window.confirm('¿Borrar esta notificación? (Soft delete: se oculta y no se elimina permanentemente.)')) {
      return
    }
    const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion
    setOcultando(notificacionId)
    setError(null)
    const success = await ocultarNotificacionContext(notificacionId)
    if (success) {
      await fetchNotificaciones()
      if (selectedNotificacion && (selectedNotificacion.id_notificacion || selectedNotificacion.idNotificacion) === notificacionId) {
        setShowDetailModal(false)
        setSelectedNotificacion(null)
      }
    } else {
      setError('No se pudo ocultar. Si el endpoint no existe en el servidor, usa eliminar o contacta al administrador.')
    }
    setOcultando(null)
  }

  // "Borrar" = soft delete (ocultar). Mantenemos la función por compatibilidad con botones existentes.
  const handleDelete = async (notificacion) => {
    return handleOcultar(notificacion)
  }

  const openDetailModal = async (notificacion) => {
    setSelectedNotificacion(notificacion)
    setShowDetailModal(true)
    
    // Marcar como leída si no lo está
    if (!notificacion.leida) {
      await handleMarcarLeida(notificacion)
    }
  }

  const getTipoIcon = (tipo) => {
    const icons = {
      'CITA': Calendar,
      'AUTORIZACION': AlertTriangle,
      'SISTEMA': Info
    }
    return icons[tipo] || Bell
  }

  const getPrioridadBadge = (prioridad) => {
    const variants = {
      'BAJA': 'default',
      'MEDIA': 'info',
      'ALTA': 'warning',
      'URGENTE': 'danger'
    }
    return <Badge variant={variants[prioridad] || 'default'}>{prioridad}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const labels = {
      'CITA': 'Cita',
      'AUTORIZACION': 'Autorización',
      'SISTEMA': 'Sistema'
    }
    return <Badge variant="primary">{labels[tipo] || tipo}</Badge>
  }

  const clearFilters = () => {
    setFiltros({ search: '', leida: '', tipo_notificacion: '', prioridad: '' })
  }

  const hasActiveFilters = filtros.search || filtros.leida !== '' || filtros.tipo_notificacion || filtros.prioridad

  if (loading && notificaciones.length === 0) {
    return <LoadingState message="Cargando notificaciones..." size="lg" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Notificaciones</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-500">
            Gestiona tus notificaciones y mantente al día
            {contadorNoLeidas > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                {contadorNoLeidas} sin leer
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2 w-full sm:w-auto">
          {contadorNoLeidas > 0 && (
            <Button 
              onClick={handleMarcarTodasLeidas} 
              variant="secondary"
              loading={marcandoTodas}
              disabled={marcandoTodas}
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Marcar todas como leídas
            </Button>
          )}
          {notificaciones.length > 0 && (
            <Button
              onClick={handleBorrarTodas}
              variant="secondary"
              loading={marcandoTodas}
              disabled={marcandoTodas}
              title="Borrar todas (soft delete)"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Borrar todas
            </Button>
          )}
          <Button onClick={fetchNotificaciones} variant="secondary">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Notificaciones push</h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 dark:text-gray-500 mb-3">
            Configura aquí las alertas push del navegador para recibir avisos en tiempo real.
          </p>
          <PushNotificationButton />
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Buscar"
                placeholder="Buscar en títulos y mensajes..."
                value={filtros.search}
                onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
                leftIcon={Search}
              />
            </div>
            <div>
              <Listbox
                label="Estado"
                options={[
                  { value: '', label: 'Todas' },
                  { value: 'false', label: 'No leídas' },
                  { value: 'true', label: 'Leídas' }
                ]}
                value={filtros.leida}
                onChange={(value) => setFiltros({ ...filtros, leida: value })}
              />
            </div>
            <div>
              <Listbox
                label="Tipo"
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'CITA', label: 'Cita' },
                  { value: 'AUTORIZACION', label: 'Autorización' },
                  { value: 'SISTEMA', label: 'Sistema' }
                ]}
                value={filtros.tipo_notificacion}
                onChange={(value) => setFiltros({ ...filtros, tipo_notificacion: value })}
              />
            </div>
            <div>
              <Listbox
                label="Prioridad"
                options={[
                  { value: '', label: 'Todas' },
                  { value: 'BAJA', label: 'Baja' },
                  { value: 'MEDIA', label: 'Media' },
                  { value: 'ALTA', label: 'Alta' },
                  { value: 'URGENTE', label: 'Urgente' }
                ]}
                value={filtros.prioridad}
                onChange={(value) => setFiltros({ ...filtros, prioridad: value })}
              />
            </div>
            {hasActiveFilters && (
              <div className="md:col-span-5 flex justify-end">
                <Button onClick={clearFilters} variant="ghost" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Notificaciones */}
      {notificaciones.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="No hay notificaciones"
          description={hasActiveFilters 
            ? "No se encontraron notificaciones con los filtros seleccionados." 
            : "No tienes notificaciones en este momento."}
        />
      ) : (
        <div className="space-y-3">
          {notificaciones.map((notificacion) => {
            const TipoIcon = getTipoIcon(notificacion.tipo_notificacion)
            const isUnread = !notificacion.leida
            
            return (
              <Card 
                key={notificacion.id_notificacion || notificacion.idNotificacion}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isUnread ? 'border-l-4 border-l-primary-500 bg-primary-50/50 dark:bg-primary-900/10' : ''
                }`}
                onClick={() => openDetailModal(notificacion)}
              >
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`flex-shrink-0 p-2 rounded-lg ${
                        isUnread 
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500'
                      }`}>
                        <TipoIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className={`text-base font-semibold ${
                                isUnread 
                                  ? 'text-gray-900 dark:text-white' 
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {notificacion.titulo}
                              </h3>
                              {isUnread && (
                                <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
                              )}
                            </div>
                            <p className={`text-sm ${
                              isUnread 
                                ? 'text-gray-700 dark:text-gray-300' 
                                : 'text-gray-600 dark:text-gray-500'
                            } line-clamp-2`}>
                              {notificacion.mensaje}
                            </p>
                            <div className="flex items-center space-x-3 mt-2">
                              {getTipoBadge(notificacion.tipo_notificacion)}
                              {getPrioridadBadge(notificacion.prioridad)}
                              <div className="flex items-center text-xs text-gray-600 dark:text-gray-500">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(notificacion.fecha_creacion)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4" onClick={(e) => e.stopPropagation()}>
                      {isUnread && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarcarLeida(notificacion)}
                          disabled={marcandoLeida === (notificacion.id_notificacion || notificacion.idNotificacion)}
                          title="Marcar como leída"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOcultar(notificacion)}
                        disabled={ocultando === (notificacion.id_notificacion || notificacion.idNotificacion)}
                        title="Borrar (soft delete)"
                      >
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(notificacion)}
                        disabled={eliminando === (notificacion.id_notificacion || notificacion.idNotificacion)}
                        title="Borrar (soft delete)"
                      >
                        <Trash2 className="w-4 h-4 text-red-700 dark:text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Paginación */}
      {pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
            <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
            <span className="font-medium">{pagination.total}</span> resultados
          </div>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
            >
              Anterior
            </Button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              Página {pagination.current_page} de {pagination.last_page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal Detalle Notificación */}
      <Modal 
        isOpen={showDetailModal} 
        onClose={() => {
          setShowDetailModal(false)
          setSelectedNotificacion(null)
        }} 
        size="md"
      >
        <ModalHeader onClose={() => {
          setShowDetailModal(false)
          setSelectedNotificacion(null)
        }}>
          <div className="flex items-center space-x-2">
            {selectedNotificacion && (() => {
              const TipoIcon = getTipoIcon(selectedNotificacion.tipo_notificacion)
              return <TipoIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            })()}
            <span>Detalle de Notificación</span>
          </div>
        </ModalHeader>
        <ModalContent>
          {selectedNotificacion && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getTipoBadge(selectedNotificacion.tipo_notificacion)}
                  {getPrioridadBadge(selectedNotificacion.prioridad)}
                  {selectedNotificacion.leida ? (
                    <Badge variant="success">
                      <MailOpen className="w-3 h-3 mr-1" />
                      Leída
                    </Badge>
                  ) : (
                    <Badge variant="info">
                      <Mail className="w-3 h-3 mr-1" />
                      No leída
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {selectedNotificacion.titulo}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                  {selectedNotificacion.mensaje}
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>
                    {formatDate(selectedNotificacion.fecha_creacion, 'long')}
                  </span>
                </div>
                {selectedNotificacion.accion_requerida && (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-500">
                    <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 text-yellow-600" />
                    <span>
                      <strong>Acción requerida:</strong> {selectedNotificacion.accion_requerida}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              {selectedNotificacion && !selectedNotificacion.leida && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleMarcarLeida(selectedNotificacion)
                  }}
                  disabled={marcandoLeida === (selectedNotificacion.id_notificacion || selectedNotificacion.idNotificacion)}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Marcar como leída
                </Button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {selectedNotificacion && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => handleOcultar(selectedNotificacion)}
                    disabled={ocultando === (selectedNotificacion.id_notificacion || selectedNotificacion.idNotificacion)}
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Borrar
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handleDelete(selectedNotificacion)}
                    disabled={eliminando === (selectedNotificacion.id_notificacion || selectedNotificacion.idNotificacion)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Borrar
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDetailModal(false)
                  setSelectedNotificacion(null)
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  )
}
